import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { base64ToArrayBuffer, formatTimeSeconds } from '../audio/audioCodec';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LOOP_SECONDS = 0.25;

// Owns playback of a single saved recording in an HTMLAudioElement,
// including the A/B loop and speed control (premium-only). Lives
// alongside the recordings library, not the multitrack engine.
//
// Caller responsibilities:
//   - Render an <audio> element with ref={player.audioRef}
//     onTimeUpdate={player.handleTimeUpdate}
//     onLoadedMetadata={player.handleLoadedMetadata}
//   - Pass a (rec) object into playRecording when the user clicks play
//
// Cleanup of the object URL is handled internally; the caller doesn't
// need to revokeObjectURL.
export const useAdvancedPlayer = () => {
  const { t } = useTranslation();
  const { isPremium } = useAuth();

  const [playingId, setPlayingId] = useState(null);
  const [loadingPlayId, setLoadingPlayId] = useState(null);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopA, setLoopA] = useState(null);
  const [loopB, setLoopB] = useState(null);
  const [showAdvancedPlayer, setShowAdvancedPlayer] = useState(false);

  const audioRef = useRef(null);

  const resetLoopPoints = useCallback(() => {
    setLoopA(null);
    setLoopB(null);
    setLoopEnabled(false);
  }, []);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingId(null);
    setCurrentTime(0);
    setShowAdvancedPlayer(false);
    resetLoopPoints();
  }, [resetLoopPoints]);

  const playRecording = useCallback(async (rec) => {
    setLoadingPlayId(rec.id);
    try {
      stopPlayback();
      const response = await axios.get(`${API}/recordings/${rec.id}`);
      const audioData = response.data.audio_data;
      const mimeType = response.data.mime_type || 'audio/webm';

      if (!audioData) throw new Error('No audio data');

      if (playbackUrl) URL.revokeObjectURL(playbackUrl);

      const base64Data = audioData.split(',')[1];
      const blob = new Blob([base64ToArrayBuffer(base64Data)], { type: mimeType });
      const url = URL.createObjectURL(blob);
      setPlaybackUrl(url);

      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.playbackRate = 1.0;
        audioRef.current.onended = () => {
          setPlayingId(null);
          setCurrentTime(0);
          setShowAdvancedPlayer(false);
        };
        audioRef.current.onerror = () => {
          toast.error(t('recorder.playError', { defaultValue: 'Errore di riproduzione' }));
          setPlayingId(null);
          setLoadingPlayId(null);
          setShowAdvancedPlayer(false);
        };
        await audioRef.current.play();
        setPlayingId(rec.id);
        setShowAdvancedPlayer(true);
        setPlaybackSpeed(1.0);
      }
    } catch (err) {
      console.error('playRecording error', err);
      toast.error(t('recorder.playError', { defaultValue: 'Errore di riproduzione' }));
      if (playbackUrl) {
        URL.revokeObjectURL(playbackUrl);
        setPlaybackUrl(null);
      }
    } finally {
      setLoadingPlayId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackUrl, stopPlayback, t]);

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const time = audioRef.current.currentTime;
    setCurrentTime(time);
    if (isPremium && loopEnabled && loopA !== null && loopB !== null) {
      if (time >= loopB) {
        audioRef.current.currentTime = loopA;
      }
    }
  }, [isPremium, loopEnabled, loopA, loopB]);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  const seek = useCallback((newTime) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, []);

  const handleSpeedChange = useCallback((speed) => {
    if (!isPremium) return;
    setPlaybackSpeed(speed);
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [isPremium]);

  const setPointA = useCallback(() => {
    if (!isPremium || !audioRef.current) return;
    const aTime = audioRef.current.currentTime;
    if (loopB !== null && aTime >= loopB - MIN_LOOP_SECONDS) {
      toast.error(t('recorder.loopABeforeB'));
      return;
    }
    setLoopA(aTime);
    toast.success(`Punto A: ${formatTimeSeconds(aTime)}`);
  }, [isPremium, loopB, t]);

  const setPointB = useCallback(() => {
    if (!isPremium || !audioRef.current) return;
    const bTime = audioRef.current.currentTime;
    if (loopA === null) {
      toast.error(t('recorder.setAFirst'));
      return;
    }
    if (bTime < loopA + MIN_LOOP_SECONDS) {
      toast.error(t('recorder.loopBAfterA'));
      return;
    }
    setLoopB(bTime);
    toast.success(`Punto B: ${formatTimeSeconds(bTime)}`);
  }, [isPremium, loopA, t]);

  // Release the cached object URL on unmount or when the URL is
  // replaced; mirrors the original RecorderPage cleanup useEffect.
  useEffect(() => {
    return () => {
      if (playbackUrl) URL.revokeObjectURL(playbackUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackUrl]);

  return {
    audioRef,
    playingId,
    loadingPlayId,
    showAdvancedPlayer,
    currentTime,
    duration,
    playbackSpeed,
    loopEnabled,
    loopA,
    loopB,
    setLoopEnabled,
    playRecording,
    stopPlayback,
    handleTimeUpdate,
    handleLoadedMetadata,
    seek,
    handleSpeedChange,
    setPointA,
    setPointB,
    resetLoopPoints,
  };
};

export default useAdvancedPlayer;
